import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all sections
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: "Individual Therapy",
      description: "Personalized one-on-one sessions tailored to your unique healing journey.",
      image: "https://images.unsplash.com/photo-1573497491208-6b1acb260507?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHx0aGVyYXB5fGVufDB8fHx8MTc1OTUxMDUwMHww&ixlib=rb-4.1.0&q=85"
    },
    {
      title: "Group Coaching",
      description: "Connect with like-minded individuals in supportive group environments.",
      image: "https://images.unsplash.com/photo-1636986905406-758b0e280f49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGVtcG93ZXJtZW50fGVufDB8fHx8MTc1OTUxMDUxMXww&ixlib=rb-4.1.0&q=85"
    },
    {
      title: "International Retreats",
      description: "Transform through immersive experiences in beautiful global destinations.",
      image: "https://images.unsplash.com/photo-1500904156668-758b0e280f49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxoZWFsaW5nfGVufDB8fHx8MTc1OTUxMDUwNnww&ixlib=rb-4.1.0&q=85"
    }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      text: "Brain Waves Ventures transformed my life. The supportive community and expert guidance helped me find my inner strength.",
      rating: 5
    },
    {
      name: "Jessica L.",
      text: "The international retreat was life-changing. I connected with amazing women from around the world and discovered my true potential.",
      rating: 5
    },
    {
      name: "Maria K.",
      text: "Rossana's approach to therapy and coaching is unlike anything I've experienced. I feel empowered and supported every step of the way.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative pt-8 pb-8 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-stone-50 opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            {/* Large White Box with Text - Less Vertical */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-12 w-full max-w-6xl min-h-64 relative">
              <div className="flex flex-col lg:flex-row items-start">
                <div className="w-full lg:w-1/3 lg:pr-8">
                  <div className="space-y-6 lg:space-y-8">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl leading-tight" style={{ color: '#0f172a' }}>
                      Life evolves,<br />
                      <span className="font-bold">so can you</span>.
                    </h1>
                    <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#0f172a' }}>
                      Stress is temporary. The results of investing in yourself are not.
                    </p>
                    <p className="text-xs sm:text-sm leading-relaxed" style={{ color: '#0f172a' }}>
                      Available in English, Spanish, and Portuguese—confidential support tailored to you.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button 
                        asChild
                        size="sm"
                        className="text-white px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 transform hover:scale-105"
                        style={{ backgroundColor: '#6a6acb' }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#5a5abb';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#6a6acb';
                        }}
                      >
                        <Link to="/services">
                          Get Started Today
                        </Link>
                      </Button>
                    </div>
                    
                    {/* Service Icons */}
                    <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 pt-4">
                      <div className="flex items-center space-x-3">
                        <svg className="w-6 h-6 lg:w-8 lg:h-8" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                        </svg>
                        <span className="text-xs font-medium text-gray-700 whitespace-nowrap">THERAPY</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <svg className="w-6 h-6 lg:w-8 lg:h-8" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                          <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
                        </svg>
                        <span className="text-xs font-medium text-gray-700 whitespace-nowrap">COACHING</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <svg className="w-6 h-6 lg:w-8 lg:h-8" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                        </svg>
                        <span className="text-xs font-medium text-gray-700 whitespace-nowrap">COMMUNITY</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full lg:w-2/3 lg:block hidden">
                  {/* Empty space for the image overlap on desktop */}
                </div>
              </div>
            </div>
            
            {/* Header Image - Overlapping on top - Desktop only */}
            <div className="absolute -top-14 right-24 z-10 hidden lg:block">
              <img 
                src="/header.png" 
                alt="Header image"
                className="w-[500px] h-[600px] object-cover rounded-2xl shadow-xl"
                style={{ borderBottomLeftRadius: '1.5rem', borderBottomRightRadius: '1.5rem' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section 
        id="about"
        ref={el => sectionRefs.current.about = el}
        className={`pt-8 pb-20 lg:py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('about') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile-first content order */}
          <div className="lg:hidden space-y-8">
            {/* Mobile: Title and subtitle */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                WELCOME TO BRAIN WAVES
              </h2>
              <h3 className="text-xl font-bold text-gray-800 mb-6">
                Change is constant. What matters is how you respond to it.
              </h3>
            </div>
            
            {/* Mobile: First paragraph */}
            <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
              <p>
                Life's transitions aren't just changes they're turning points that shape who we are. Science shows that resilience isn't fixed; it's built with the right tools, practices, and support.
              </p>
            </div>
            
            {/* Mobile: Purple image */}
            <div className="relative">
              <img 
                src="/purple_picture.png" 
                alt="Brain activity visualization"
                className="w-full h-80 object-cover rounded-2xl shadow-xl"
              />
            </div>
            
            {/* Mobile: Long paragraph text (moved below purple image) */}
            <div className="space-y-4 text-sm text-gray-700">
              <p className="leading-relaxed">
                At Brain Waves Ventures, we provide those tools. Our programs integrate therapy-based coaching, structured strategies, and immersive experiences to create measurable, lasting progress. In a confidential setting, we combine expertise, cultural awareness, and action-oriented methods—helping you navigate change with clarity and confidence.
              </p>
            </div>
            
            {/* Mobile: Flower image */}
            <div className="relative">
              <img 
                src="/website flowe.png" 
                alt="Minimalist interior design"
                className="w-full h-80 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
          
          {/* Desktop layout */}
          <div className="hidden lg:grid grid-cols-2 gap-16 items-start">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  WELCOME TO BRAIN WAVES
                </h2>
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Change is constant. What matters is how you respond to it.
                </h3>
              </div>
              
              <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                <p>
                  Life's transitions aren't just changes they're turning points that shape who we are. Science shows that resilience isn't fixed; it's built with the right tools, practices, and support.
                </p>
              </div>
              
              {/* Purple Picture - Underneath the text on the left */}
              <div className="relative">
                <img 
                  src="/purple_picture.png" 
                  alt="Brain activity visualization"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
              </div>
            </div>

            {/* Right Column - Images and Text */}
            <div className="flex flex-col justify-center h-full">
              {/* Top Image - Minimalist Interior */}
              <div className="relative mb-8">
                <img 
                  src="/website flowe.png" 
                  alt="Minimalist interior design"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
              </div>
              
              {/* Bottom Row - Text Content - Desktop only */}
              <div className="space-y-4 text-sm text-gray-700">
                <p className="leading-relaxed">
                  At Brain Waves Ventures, we provide those tools. Our programs integrate therapy-based coaching, structured strategies, and immersive experiences to create measurable, lasting progress. In a confidential setting, we combine expertise, cultural awareness, and action-oriented methods—helping you navigate change with clarity and confidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="services"
        ref={el => sectionRefs.current.services = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('services') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
          </div>
          
          {/* Services Cards Grid */}
          <div className="space-y-8">
            {/* First row - 3 cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Traditional Therapy Card */}
              <div 
                className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer"
                onClick={() => {
                  navigate('/services');
                  window.scrollTo(0, 0);
                }}
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'rgba(106, 106, 203, 0.1)' }}>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors duration-300">Traditional Therapy</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Using evidence-based psychological frameworks and practical tools to help you uncover patterns and design strategies for sustainable progress.
                  </p>
                </div>
              </div>

              {/* One-on-One Coaching Card */}
              <div 
                className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer"
                onClick={() => {
                  navigate('/services');
                  window.scrollTo(0, 0);
                }}
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'rgba(106, 106, 203, 0.1)' }}>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                      <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors duration-300">One-on-One Coaching</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Private coaching sessions that provide focus, accountability, and structure to help you clarify priorities and create actionable plans.
                  </p>
                </div>
              </div>

              {/* The Safe Space Card */}
              <div 
                className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer"
                onClick={() => {
                  navigate('/services');
                  window.scrollTo(0, 0);
                }}
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'rgba(106, 106, 203, 0.1)' }}>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                      <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors duration-300">The Safe Space (Pro-Bono)</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    A confidential, free group designed to support women in moments of crisis. A place where you can exhale, be heard, and begin to heal.
                  </p>
                </div>
              </div>
            </div>

            {/* Second row - 2 cards centered */}
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl">
                {/* IWC Membership Card */}
                <div 
                  className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer"
                  onClick={() => {
                    navigate('/memberships');
                    window.scrollTo(0, 0);
                  }}
                >
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'rgba(106, 106, 203, 0.1)' }}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                        <path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors duration-300">IWC Membership</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      A private global community designed for women navigating transitions. Access curated experiences that transform insight into momentum.
                    </p>
                  </div>
                </div>

                {/* Resource Library Card */}
                <div 
                  className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer"
                  onClick={() => {
                    navigate('/library');
                    window.scrollTo(0, 0);
                  }}
                >
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'rgba(106, 106, 203, 0.1)' }}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: '#6a6acb' }}>
                        <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors duration-300">Resource Library</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      A place to find what you need, when you need it. Comprehensive resources with practical tools, emergency contacts, and trusted connections.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        id="testimonials"
        ref={el => sectionRefs.current.testimonials = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('testimonials') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Our Community Says
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from women who have transformed their lives with us.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow duration-300">
                <CardContent className="space-y-4">
                  <div className="flex justify-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-current" style={{ color: 'rgba(247, 160, 121, 1)' }} />
                    ))}
                  </div>
                  <p className="text-gray-600 italic leading-relaxed">"{testimonial.text}"</p>
                  <p className="font-semibold" style={{ color: 'rgba(50, 57, 241, 1)' }}>
                    - {testimonial.name}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta"
        ref={el => sectionRefs.current.cta = el}
        className={`bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('cta') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="w-full h-px bg-black"></div>
        <div className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Ready to Begin Your <span className="relative inline-block">
                <span className="relative z-10">Transformation</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-200 to-purple-200 opacity-30 rounded-lg transform -rotate-1"></span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 opacity-50 rounded-lg transform rotate-1"></span>
              </span>?
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Join our global community of empowered women and professionals. Your journey to healing, growth, and connection starts today.
            </p>
            <button 
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
              style={{ 
                borderColor: '#6a6acb', 
                color: '#6a6acb',
                backgroundColor: 'transparent'
              }} 
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#6a6acb';
                e.target.style.color = 'white';
              }} 
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6a6acb';
              }}
            >
              Get Started Today →
            </button>
          </div>
        </div>
        <div className="w-full h-px bg-black"></div>
      </section>
    </div>
  );
};

export default Home;
